import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getSiteConfig } from '../config/sites';
import { apiGet } from '../api/readerApi';

const CACHE_KEY_PREFIX = 'jeshua_site_config_';

const readCache = (site) => {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY_PREFIX + site);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeCache = (site, data) => {
  try {
    window.sessionStorage.setItem(CACHE_KEY_PREFIX + site, JSON.stringify(data));
  } catch (error) { /* storage unavailable */ }
};

const mergeSiteConfig = (fallback, remote) => {
  if (!remote) return fallback;
  const contact = remote.contact || {};
  const hasWhatsapp = contact.whatsappNumber || contact.whatsappUrl;
  return {
    ...fallback,
    email: contact.email || fallback.email,
    whatsapp: hasWhatsapp
      ? { number: contact.whatsappNumber || '', url: contact.whatsappUrl || '' }
      : fallback.whatsapp,
    legalName: remote.legalName ?? fallback.legalName,
    taxId: remote.taxId ?? fallback.taxId,
    address: remote.address ?? fallback.address,
    languages:
      Array.isArray(remote.languages) && remote.languages.length > 0
        ? remote.languages
        : fallback.languages,
    defaultLanguage: remote.defaultLanguage || fallback.defaultLanguage,
    stats: remote.stats || null
  };
};

const SiteContext = createContext(null);

export function SiteProvider({ children }) {
  const fallback = useMemo(() => getSiteConfig(), []);
  const [remote, setRemote] = useState(() => readCache(fallback.site));

  useEffect(() => {
    if (remote) return undefined;
    let cancelled = false;
    apiGet(`/sites/${fallback.site}`).then((result) => {
      if (cancelled || !result.ok || !result.data) return;
      writeCache(fallback.site, result.data);
      setRemote(result.data);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const config = useMemo(() => mergeSiteConfig(fallback, remote), [fallback, remote]);

  return <SiteContext.Provider value={config}>{children}</SiteContext.Provider>;
}

export const useSite = () => {
  const context = useContext(SiteContext);
  return context || getSiteConfig();
};
